import Keycloak from "keycloak-js";

import { clearShoppingCarts } from "./shoppingCartStorage";

export const keycloak = new Keycloak({
  url: `${process.env.REACT_APP_KEYCLOAK_HOST}/auth`,
  realm: process.env.REACT_APP_KEYCLOAK_REALM_NAME as string,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID as string,
});

/** A token that expires within this many seconds is refreshed before it is sent. */
export const MIN_TOKEN_VALIDITY_SECONDS = 30;

/**
 * A sign-in round trip takes seconds and yields a fresh token. Needing another one this soon means signing in did
 * not help (for example a backend that rejects valid tokens), so the next attempt stops on the error screen instead
 * of looping app -> Keycloak -> app (Keycloak signs the user in silently while its SSO session lasts).
 */
export const SIGN_IN_AGAIN_WINDOW_MS = 2 * 60 * 1000;
const LAST_SIGN_IN_ATTEMPT_KEY = "ngft-dashboard.signInAgainAt";

export type AuthErrorReason = "init_failed" | "not_authenticated" | "refresh_failed" | "api_unauthorized";

export class SessionExpiredError extends Error {
  readonly status = 401;
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

let authErrorHandler: ((reason: AuthErrorReason) => void) | null = null;
let refreshInFlight: { promise: Promise<boolean>; forced: boolean } | null = null;
/** signInAgain runs once per page load; parallel 401s and refresh failures must not race into the error screen. */
let signInAgainStarted = false;

// A page restored from the back/forward cache is a new visit, but keeps this module's state: allow a new attempt.
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event: PageTransitionEvent) => {
    if (event.persisted) {
      signInAgainStarted = false;
    }
  });
}

/** index.tsx registers the function that replaces the app with the auth error screen. */
export const setAuthErrorHandler = (handler: ((reason: AuthErrorReason) => void) | null): void => {
  authErrorHandler = handler;
};

/**
 * Standard flow (authorization code) with PKCE. The implicit flow gave no refresh token, so the token could never be
 * refreshed and expired under the user after its lifetime.
 */
export const initAuth = async (): Promise<boolean> => {
  keycloak.onTokenExpired = () => {
    refreshToken().catch(() => signInAgain("refresh_failed"));
  };
  return keycloak.init({
    onLoad: "login-required",
    pkceMethod: "S256",
    enableLogging: process.env.NODE_ENV === "development",
  });
};

/**
 * Refreshes the access token when it expires within MIN_TOKEN_VALIDITY_SECONDS (`force`: always). Concurrent callers
 * share one refresh request, except that a forced call never joins a routine one: that resolves without refreshing
 * while the token still looks valid by the client clock, which is exactly the token the API has just rejected.
 */
export const refreshToken = ({ force = false }: { force?: boolean } = {}): Promise<boolean> => {
  if (refreshInFlight && (refreshInFlight.forced || !force)) {
    return refreshInFlight.promise;
  }
  const refresh = {
    forced: force,
    promise: keycloak.updateToken(force ? -1 : MIN_TOKEN_VALIDITY_SECONDS).finally(() => {
      if (refreshInFlight === refresh) {
        refreshInFlight = null;
      }
    }),
  };
  refreshInFlight = refresh;
  return refresh.promise;
};

/**
 * A valid access token for an API call. When it cannot be refreshed, the user signs in again (guarded) and this
 * rejects with SessionExpiredError: an expired token is never handed out.
 */
export const getToken = async (): Promise<string> => {
  try {
    await refreshToken();
  } catch (e) {
    signInAgain("refresh_failed");
    throw new SessionExpiredError();
  }
  if (!keycloak.token) {
    signInAgain("refresh_failed");
    throw new SessionExpiredError();
  }
  return keycloak.token;
};

/**
 * After an API 401 for `usedToken`: refreshes the token once (unless another request already did) so the caller can
 * retry. Signs in again (guarded) and rejects when that is not possible.
 */
export const refreshAfterUnauthorized = async (usedToken: string): Promise<void> => {
  try {
    if (keycloak.token === usedToken) {
      await refreshToken({ force: true });
    }
  } catch (e) {
    signInAgain("api_unauthorized");
    throw new SessionExpiredError();
  }
};

/**
 * The only way the app re-signs-in on its own: redirects to Keycloak's login, unless the previous attempt was within
 * SIGN_IN_AGAIN_WINDOW_MS; then it shows the auth error screen instead, so a failure that survives a sign-in cannot
 * loop. Runs once per page load (later calls are no-ops, reset on a back/forward-cache restore). Never a global
 * (SSO) logout.
 */
export const signInAgain = (reason: AuthErrorReason): void => {
  if (signInAgainStarted) {
    return;
  }
  signInAgainStarted = true;
  if (isRepeatedSignInAttempt()) {
    authErrorHandler?.(reason);
    return;
  }
  keycloak.login().catch(() => authErrorHandler?.(reason));
};

const isRepeatedSignInAttempt = (): boolean => {
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(LAST_SIGN_IN_ATTEMPT_KEY));
    window.sessionStorage.setItem(LAST_SIGN_IN_ATTEMPT_KEY, String(now));
    return now - last < SIGN_IN_AGAIN_WINDOW_MS;
  } catch (e) {
    // No sessionStorage (blocked site data): sign in without the loop guard rather than strand every such user on
    // the error screen.
    return false;
  }
};

export const isLoggedIn = (): boolean => !!keycloak.authenticated;

// Keycloak id of the signed-in user (the token's `sub`), e.g. the user ngft-iam stores notifications for.
export const getUserSub = (): string | null => keycloak.tokenParsed?.sub ?? null;

/** Explicit logout from the logout button: clears per-user browser data, then ends the Keycloak session. */
export const logout = async (): Promise<void> => {
  clearShoppingCarts();
  await keycloak.logout();
};

/** Test seam: resets the module state between tests. */
export const resetAuthStateForTests = (): void => {
  authErrorHandler = null;
  refreshInFlight = null;
  signInAgainStarted = false;
};
